import * as THREE from 'three';
import { settings } from '@/config';
import type { Renderer } from '@/renderer/Renderer';

const vertexShader = /* glsl */ `
  varying vec3 vWorldPosition;

  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

// Above the horizon: horizon -> middle -> top as height increases. Below it:
// horizon -> bottom. Both blends use `smoothstep` for a soft, banded-free gradient
// (no hard seam at the horizon line itself, since both sides start from the same
// `uHorizon` colour).
const fragmentShader = /* glsl */ `
  uniform vec3 uTop;
  uniform vec3 uMiddle;
  uniform vec3 uHorizon;
  uniform vec3 uBottom;
  uniform float uStoryDark;
  uniform float uStoryTime;
  uniform vec3 uStoryVeil;
  uniform float uStoryVeilAmt;
  uniform float uStoryEmberAmt;
  uniform float uStoryScale;
  uniform float uStoryLineDensity;
  uniform float uStoryLineSpeed;
  uniform float uStoryLineWidth;
  uniform vec3 uStoryLineColor;
  uniform vec3 uStoryLineCore;
  uniform float uStoryPulse;

  varying vec3 vWorldPosition;

  /* Cheap value-noise fbm for the STORY sky - the dome covers the whole frame, so this stays
     4 octaves of 2D value noise, far cheaper than simplex. NO BACKTICKS in these comments. */
  float skyHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
  float skyNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(skyHash(i), skyHash(i + vec2(1.0, 0.0)), u.x),
      mix(skyHash(i + vec2(0.0, 1.0)), skyHash(i + vec2(1.0, 1.0)), u.x),
      u.y);
  }
  float skyFbm(vec2 p) {
    float s = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      s += a * skyNoise(p);
      p = p * 2.13 + vec2(7.3, 3.1);
      a *= 0.5;
    }
    return s;
  }

  void main() {
    float h = normalize(vWorldPosition).y;
    vec3 color;
    if (h >= 0.0) {
      color = mix(uHorizon, uMiddle, smoothstep(0.0, 0.35, h));
      color = mix(color, uTop, smoothstep(0.3, 1.0, h));
    } else {
      color = mix(uHorizon, uBottom, smoothstep(0.0, -0.6, h));
    }

    /* STORY-MODE FLUID SKY (2026-09-02, user: "in shader mode I want the sky more crazy -
       something fluid moving in the sky, more fantasy"). Domain-warped fbm veils drift and
       fold through the night dome like slow liquid smoke; the ridge crests catch a faint
       ember light so the sky and the orange emblem energy on the architecture read as one
       world. Planar projection of the view direction (no atan seam), above-horizon only. */
    if (uStoryDark > 0.001 && h > 0.0) {
      vec3 d = normalize(vWorldPosition);
      vec2 sp = d.xz * (1.7 / (0.45 + d.y));
      float t = uStoryTime;
      vec2 warp = vec2(
        skyFbm(sp * 0.9 + vec2(t * 0.35, -t * 0.22)),
        skyFbm(sp * 0.9 + vec2(-t * 0.27, t * 0.31) + 11.7));
      float n = skyFbm(sp * uStoryScale + (warp - 0.5) * 2.2 + vec2(t * 0.5, t * 0.13));
      float ridge = pow(1.0 - abs(n * 2.0 - 1.0), 3.0);
      float band = smoothstep(0.02, 0.3, d.y);
      vec3 story = color;
      story += uStoryVeil * (smoothstep(0.35, 0.95, n) * 0.8 + ridge * 0.5) * band * uStoryVeilAmt;
      /* MESH-STYLE EMISSION LINES (2026-09-02 v2, user: "make it inverse - the mesh shader on
         the sky"): the travelling orange-and-black iso-bands that used to trace the
         architecture now flow across the dome - thin burning contour lines drifting through
         the night, riding the same warped field as the veils. uStoryEmberAmt is their
         strength (the old ember-crest treatment moved DOWN onto the meshes - storyGrade). */
      float lineField = fract(n * uStoryLineDensity - t * uStoryLineSpeed * 2.5);
      float lineD = abs(lineField - 0.5) * 2.0;
      float lineB = 1.0 - smoothstep(0.0, uStoryLineWidth, lineD);
      lineB *= lineB;
      /* uStoryPulse - the sky's lines breathe with the narrator too (see storyGrade). */
      story += mix(uStoryLineColor, uStoryLineCore, lineB) * lineB * band
             * uStoryEmberAmt * (0.3 + 0.7 * uStoryPulse);
      color = mix(color, story, uStoryDark);
    }

    gl_FragColor = vec4(color, 1.0);
  }
`;

/**
 * Flat, art-directed 4-stop vertical gradient sky (top/middle/horizon/bottom) — a
 * huge inward-facing sphere the camera is always inside of. No atmospheric
 * simulation (turbidity/rayleigh/mie/sun-disc); replaced the previous physically-based
 * `THREE.Sky` (2026-07-05, user: it could blow out to solid white at low sun angles
 * and they wanted the simpler flat gradient back instead).
 */
/** Scratch colour for {@link Sky.setStoryDark} - avoids allocating per frame. */
const SKY_SCRATCH = new THREE.Color();

export class Sky {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private envTarget: THREE.WebGLRenderTarget;

  constructor(
    private readonly renderer: Renderer,
    private readonly scene: THREE.Scene,
  ) {
    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTop: { value: new THREE.Color(settings.sky.top) },
        uMiddle: { value: new THREE.Color(settings.sky.middle) },
        uHorizon: { value: new THREE.Color(settings.sky.horizon) },
        uBottom: { value: new THREE.Color(settings.sky.bottom) },
        uStoryDark: { value: 0 },
        uStoryTime: { value: 0 },
        uStoryVeil: { value: new THREE.Color(settings.storyMode.grade.skyFlow.veilColor) },
        uStoryVeilAmt: { value: settings.storyMode.grade.skyFlow.intensity },
        uStoryEmberAmt: { value: settings.storyMode.grade.skyFlow.emberAmount },
        uStoryScale: { value: settings.storyMode.grade.skyFlow.scale },
        // The sky's flowing lines share the mesh line look (`grade.lines`) so the swap stays
        // one visual language — refreshed in setStoryDark alongside everything else.
        uStoryLineDensity: { value: settings.storyMode.grade.lines.density },
        uStoryLineSpeed: { value: settings.storyMode.grade.lines.speed },
        uStoryLineWidth: { value: settings.storyMode.grade.lines.width },
        uStoryLineColor: { value: new THREE.Color(settings.storyMode.grade.lines.color) },
        uStoryLineCore: { value: new THREE.Color(settings.storyMode.grade.lines.coreColor) },
        uStoryPulse: { value: settings.storyMode.grade.pulse.idle },
      },
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    });

    // GOTCHA: this sphere is centred on the WORLD ORIGIN and does NOT follow the camera, so
    // `settings.camera.far` must cover (camera's max distance from origin) + THIS radius, or
    // the far side of the dome gets clipped and the renderer's flat `clearColor` shows through
    // the hole. That has now bitten twice:
    //  - 2026-07-05: radius entirely beyond `far` ⇒ NO gradient anywhere, just clear colour.
    //  - 2026-07-17: `far` was 1000 while the baked camera travels ~496 units from origin, so
    //    from ~frame 105 the dome's far side (up to 1396) was cut off — a pale, faceted white
    //    polygon (the clear colour) appeared mid-scene. Fixed by raising `far` to 1600.
    // If the camera path ever gets longer, raise `far` again (or make this dome follow the
    // camera, which would also need the shader to shade by LOCAL direction, not world position).
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(900, 32, 16), this.material);
    this.mesh.renderOrder = -1;
    scene.add(this.mesh);
    scene.background = null; // the mesh itself is the visible backdrop

    this.envTarget = new THREE.WebGLRenderTarget(1, 1); // placeholder until first rebuild
    this.rebuild();
  }

  /** Push the current `settings.sky` colours + regenerate the PMREM reflection env. */
  /**
   * Blend the sky toward the story's night palette (2026-08-18, user: "sky changes to dark").
   *
   * `k` 0 = the authored sky, 1 = fully the story palette. The AUTHORED colours are re-read from
   * settings on every call rather than snapshotted, so this can never strand the dome on a
   * half-graded colour if a story is interrupted, and the sky GUI keeps working while a story is
   * open. The dome is a custom ShaderMaterial with no `<map_fragment>` chunk, which is why it is
   * graded here by uniform rather than by the Perlin dissolve the baked materials use.
   */
  setStoryDark(k: number): void {
    const base = settings.sky;
    const night = settings.storyMode.grade.sky;
    const u = this.material.uniforms;
    const blend = (name: 'top' | 'middle' | 'horizon' | 'bottom', uniform: string) => {
      const color = u[uniform]!.value as THREE.Color;
      color.set(base[name]).lerp(SKY_SCRATCH.set(night[name]), k);
    };
    blend('top', 'uTop');
    blend('middle', 'uMiddle');
    blend('horizon', 'uHorizon');
    blend('bottom', 'uBottom');
    // The fluid veils + flowing lines (see the fragment shader) arrive and leave with the same
    // k, and their look is re-read from settings so the debug GUI tunes them live.
    const flow = settings.storyMode.grade.skyFlow;
    const lines = settings.storyMode.grade.lines;
    u.uStoryDark!.value = k;
    (u.uStoryVeil!.value as THREE.Color).set(flow.veilColor);
    u.uStoryVeilAmt!.value = flow.intensity;
    u.uStoryEmberAmt!.value = flow.emberAmount;
    u.uStoryScale!.value = flow.scale;
    u.uStoryLineDensity!.value = lines.density;
    u.uStoryLineSpeed!.value = lines.speed;
    u.uStoryLineWidth!.value = lines.width;
    (u.uStoryLineColor!.value as THREE.Color).set(lines.color);
    (u.uStoryLineCore!.value as THREE.Color).set(lines.coreColor);
  }

  /** Advance the story sky's fluid drift. Cheap no-op while the grade is closed. */
  update(delta: number): void {
    const u = this.material.uniforms;
    if ((u.uStoryDark!.value as number) <= 0) return;
    u.uStoryTime!.value += delta * settings.storyMode.grade.skyFlow.speed;
  }

  /** The voice pulse (0..1) — set every tick by Experience while a story is open. */
  setStoryPulse(v: number): void {
    this.material.uniforms.uStoryPulse!.value = v;
  }

  rebuild(): void {
    const cfg = settings.sky;
    (this.material.uniforms.uTop!.value as THREE.Color).set(cfg.top);
    (this.material.uniforms.uMiddle!.value as THREE.Color).set(cfg.middle);
    (this.material.uniforms.uHorizon!.value as THREE.Color).set(cfg.horizon);
    (this.material.uniforms.uBottom!.value as THREE.Color).set(cfg.bottom);

    if (this.envTarget) this.envTarget.dispose();
    const pmrem = new THREE.PMREMGenerator(this.renderer.instance);
    this.envTarget = pmrem.fromScene(this.scene);
    this.scene.environment = this.envTarget.texture;
    pmrem.dispose();
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.envTarget.dispose();
  }
}
